import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { 
  TrendingUp, Users, FileText, AlertTriangle, Calendar, Filter, Download, Briefcase, Loader2
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function ReportesBI({ usuario, onBackToMenu }) {
  const [moduloActivo, setModuloActivo] = useState('rrhh');
  const [filtroTiempo, setFiltroTiempo] = useState('historico'); 
  const [filtroSede, setFiltroSede] = useState('todas');
  
  const [cargando, setCargando] = useState(true);
  const [datosEvals, setDatosEvals] = useState([]);
  const [datosDocs, setDatosDocs] = useState([]);
  const [listaSedes, setListaSedes] = useState([]);

  const printRef = useRef();

  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) 
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed;
    } catch (e) { 
      return {}; 
    }
  }, [usuario]);

  useEffect(() => {
    const cargarDatosCentrales = async () => {
      setCargando(true);
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (usuario) {
          headers['x-user-data'] = JSON.stringify(usuario);
          headers['x-abac-permissions'] = JSON.stringify(permisosABAC);
        }

        const cargoUpper = String(usuario?.cargo || "").toUpperCase();
        const esAdminGlobal = cargoUpper.includes("ADMIN") || cargoUpper.includes("SUPER") || cargoUpper.includes("GERENTE") || cargoUpper.includes("DIRECTOR");        
        const alcanceABAC = permisosABAC["Ver Documentos"]?.alcance || (esAdminGlobal ? "GLOBAL" : "PROPIA");
        const sedeConsulta = alcanceABAC === 'GLOBAL' ? "" : (usuario?.codigo_bodega || "");

        const [resEvals, resDocs, resSedes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/evaluaciones`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL}/api/documentos-legales?sede_id=${sedeConsulta}&alcance=${alcanceABAC}`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/sedes`, { headers })
        ]);

        const jsonEvals = await resEvals.json();
        const jsonDocs = await resDocs.json();
        const jsonSedes = await resSedes.json();

        if (jsonEvals.success) setDatosEvals(jsonEvals.evaluaciones || jsonEvals.data || []);
        if (jsonDocs.success) setDatosDocs(jsonDocs.data || jsonDocs.documentos || []);
        if (jsonSedes.success) setListaSedes(jsonSedes.sedes || jsonSedes.data || []);

      } catch (error) {
        console.error("Error al cargar datos del BI:", error);
      } finally {
        setCargando(false);
      }
    };

    if (usuario) {
      cargarDatosCentrales();
    }
  }, [usuario, permisosABAC]);

  const { kpisRRHH, kpisLegal, graficosRRHH, graficosLegal } = useMemo(() => {
    const hoy = new Date();
    const currentYear = hoy.getFullYear();
    const currentMonth = hoy.getMonth();

    const evalsFiltradas = datosEvals.filter(ev => {
      if (filtroSede !== 'todas') {
        const coincideId = String(ev.sede_id) === String(filtroSede);
        const coincideNombre = String(ev.centroTrabajo).toLowerCase().includes(String(filtroSede).toLowerCase());
        if (!coincideId && !coincideNombre) return false;
      }
      
      if (filtroTiempo !== 'historico') {
        const fechaCruda = ev.fecha_evaluacion || ev.fecha || ev.created_at;
        if (!fechaCruda) return false;
        
        const fechaEv = new Date(fechaCruda);
        if (filtroTiempo === 'este_mes') {
          return fechaEv.getMonth() === currentMonth && fechaEv.getFullYear() === currentYear;
        } else if (filtroTiempo === 'este_trimestre') {
          const currentQuarter = Math.floor(currentMonth / 3);
          const evQuarter = Math.floor(fechaEv.getMonth() / 3);
          return evQuarter === currentQuarter && fechaEv.getFullYear() === currentYear;
        } else if (filtroTiempo === 'este_ano') {
          return fechaEv.getFullYear() === currentYear;
        }
      }
      return true; 
    });

    const totalEvals = evalsFiltradas.length;
    const sumaPuntajes = evalsFiltradas.reduce((sum, ev) => sum + Number(ev.puntaje || ev.porcentaje || 0), 0);
    const promedioGlobal = totalEvals > 0 ? (sumaPuntajes / totalEvals).toFixed(1) : 0;
    
    const enPlanMejora = evalsFiltradas.filter(ev => Number(ev.puntaje || ev.porcentaje || 0) < 70).length;
    const altosPotenciales = evalsFiltradas.filter(ev => Number(ev.puntaje || ev.porcentaje || 0) >= 90).length;

    const agruparSedes = {};
    evalsFiltradas.forEach(ev => {
      const nombreSede = ev.centroTrabajo || ev.sede_id || 'Sin Sede';
      if (!agruparSedes[nombreSede]) agruparSedes[nombreSede] = { sum: 0, count: 0 };
      agruparSedes[nombreSede].sum += Number(ev.puntaje || ev.porcentaje || 0);
      agruparSedes[nombreSede].count++;
    });
    
    const dataDesempeno = Object.keys(agruparSedes).map(sede => ({
      sede: String(sede).substring(0, 15),
      promedio: Number((agruparSedes[sede].sum / agruparSedes[sede].count).toFixed(1)),
      evaluaciones: agruparSedes[sede].count
    }));

    const nombresMeses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const agruparMeses = Array.from({ length: 12 }, (_, i) => ({ mes: nombresMeses[i], sum: 0, count: 0 }));
    
    evalsFiltradas.forEach(ev => {
      const fechaCruda = ev.fecha_evaluacion || ev.fecha || ev.created_at;
      if (fechaCruda) {
        const fechaEv = new Date(fechaCruda);
        const m = fechaEv.getMonth();
        agruparMeses[m].sum += Number(ev.puntaje || ev.porcentaje || 0);
        agruparMeses[m].count++;
      }
    });
    const dataTendenciaAnual = agruparMeses.map(m => ({
      mes: m.mes,
      puntaje: m.count > 0 ? Number((m.sum / m.count).toFixed(1)) : 0
    })).filter(m => m.puntaje > 0); 

    // --- FILTRADO ROBUSTO DE DOCUMENTOS LEGALES ---
    const docsFiltrados = datosDocs.filter(doc => {
      if (filtroSede !== 'todas') {
        const coincideId = String(doc.sede_id) === String(filtroSede);
        // Aseguramos capturar también si viene por nombre
        const coincideNombre = String(doc.nombre_sede || '').toLowerCase().includes(String(filtroSede).toLowerCase());
        if (!coincideId && !coincideNombre) return false;
      }
      return true;
    });

    let vigentes = 0;
    let porVencer = 0;
    let vencidos = 0;

    docsFiltrados.forEach(doc => {
      if (!doc.fecha_vencimiento) {
        vigentes++; 
        return;
      }
      const fechaVenc = new Date(doc.fecha_vencimiento);
      const diffTime = fechaVenc - hoy;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) vencidos++;
      else if (diffDays <= 30) porVencer++;
      else vigentes++;
    });

    const dataDocumentos = [
      { name: 'Vigentes', value: vigentes, color: '#10b981' },
      { name: 'Por Vencer (30 días)', value: porVencer, color: '#f59e0b' },
      { name: 'Vencidos', value: vencidos, color: '#ef4444' },
    ].filter(d => d.value > 0); 

    return {
      kpisRRHH: { promedioGlobal, totalEvals, enPlanMejora, altosPotenciales },
      kpisLegal: { vigentes, porVencer, vencidos, total: docsFiltrados.length },
      graficosRRHH: { dataDesempeno, dataTendenciaAnual },
      graficosLegal: { dataDocumentos }
    };
  }, [datosEvals, datosDocs, filtroTiempo, filtroSede]);

  const exportarPDF = async () => {
    const element = printRef.current;
    if (!element) {
      alert("No se encontró el contenido para exportar.");
      return;
    }
    
    try {
      const originalOverflow = element.style.overflow;
      const originalHeight = element.style.height;
      
      element.style.overflow = 'visible';
      element.style.height = 'auto';
      
      const canvas = await html2canvas(element, { 
        scale: 2, 
        backgroundColor: '#0f172a', 
        useCORS: true,
        logging: false 
      });
      
      element.style.overflow = originalOverflow;
      element.style.height = originalHeight;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); 
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Reporte_Gerencial_${moduloActivo.toUpperCase()}_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (err) {
      console.error("Error exportando a PDF:", err);
      alert("Hubo un problema procesando el gráfico. Revisa la consola.");
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/90 backdrop-blur-md border border-white/20 p-4 rounded-xl shadow-2xl">
          <p className="text-white font-bold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm font-semibold">
              {entry.name}: {entry.value} {entry.name.includes('Promedio') || entry.name.includes('Puntaje') ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full min-h-screen py-8 px-4 flex flex-col items-center animate-fade-in relative z-10">
      <div className="w-full max-w-7xl bg-white/10 backdrop-blur-3xl border border-white/20 shadow-2xl rounded-[34px] p-6 sm:p-8 relative overflow-hidden flex flex-col h-[90vh]">
        
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 z-20"></div>
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 relative z-10 gap-4">
          <div>
            <h2 className="text-3xl font-black text-white tracking-tight drop-shadow-md flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-indigo-400" />
              Corporate Analytics & BI
            </h2>
            <p className="text-sm font-bold text-slate-300 mt-1">Panel de control gerencial e inteligencia de negocios</p>
          </div>

          <div className="flex gap-3">
            <div className="flex bg-black/20 p-1 rounded-xl border border-white/10 backdrop-blur-md">
              <button onClick={() => setModuloActivo('rrhh')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${moduloActivo === 'rrhh' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-300 hover:bg-white/10'}`}>
                👥 RR.HH
              </button>
              <button onClick={() => setModuloActivo('legal')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${moduloActivo === 'legal' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-300 hover:bg-white/10'}`}>
                ⚖️ Legal & SST
              </button>
              <button onClick={() => setModuloActivo('finanzas')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${moduloActivo === 'finanzas' ? 'bg-amber-600 text-white shadow-md' : 'text-slate-300 hover:bg-white/10'}`}>
                💰 Financiero
              </button>
            </div>
            <button onClick={onBackToMenu} className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl border border-white/20 transition cursor-pointer">
              ✕ Cerrar
            </button>
          </div>
        </div>

        <div className="flex gap-4 mb-6 relative z-10 bg-white/5 p-3 rounded-2xl border border-white/10 items-center justify-between">
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-indigo-300" />
            <span className="text-xs font-bold text-white uppercase tracking-wider hidden md:inline">Filtros:</span>
            
            <select 
              value={filtroTiempo}
              onChange={(e) => setFiltroTiempo(e.target.value)}
              className="bg-black/40 border border-white/20 text-white text-xs font-bold px-3 py-2 rounded-xl focus:outline-none focus:border-indigo-400 cursor-pointer"
            >
              <option value="historico">📊 Histórico Completo</option>
              <option value="este_ano">📅 Año Fiscal {new Date().getFullYear()}</option>
              <option value="este_trimestre">📆 Este Trimestre</option>
              <option value="este_mes">🗓️ Este Mes</option>
            </select>

            <select 
              value={filtroSede}
              onChange={(e) => setFiltroSede(e.target.value)}
              className="bg-black/40 border border-white/20 text-white text-xs font-bold px-3 py-2 rounded-xl focus:outline-none focus:border-indigo-400 cursor-pointer max-w-[200px]"
            >
              <option value="todas">🏢 Todas las Sedes</option>
              {listaSedes.map(s => (
                <option key={s.id || s.codigo_bodega} value={s.codigo_bodega || s.nombre}>{s.nombre}</option>
              ))}
            </select>
          </div>
          
          <button onClick={exportarPDF} className="flex items-center gap-2 text-xs font-black text-indigo-300 hover:text-white transition cursor-pointer bg-white/10 px-4 py-2 rounded-xl border border-indigo-500/30 hover:bg-indigo-600 shadow-md">
            <Download className="w-4 h-4" /> Exportar PDF
          </button>
        </div>

        {cargando ? (
          <div className="flex-1 flex flex-col items-center justify-center relative z-10">
            <Loader2 className="w-12 h-12 text-indigo-400 animate-spin mb-4" />
            <h3 className="text-white font-bold">Procesando Base de Datos en Tiempo Real...</h3>
          </div>
        ) : (
          <div ref={printRef} className="flex-1 overflow-y-auto pr-2 custom-scrollbar relative z-10 space-y-6 pb-6">
            
            {moduloActivo === 'rrhh' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-md shadow-lg flex flex-col justify-center">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-[10px] font-black uppercase tracking-wider">Promedio Global</span>
                      <div className="p-1.5 bg-emerald-500/20 rounded-lg"><TrendingUp className="w-4 h-4 text-emerald-400" /></div>
                    </div>
                    <h3 className="text-3xl font-black text-white">{kpisRRHH.promedioGlobal}%</h3>
                  </div>
                  
                  <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-md shadow-lg flex flex-col justify-center">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-[10px] font-black uppercase tracking-wider">Evals. Realizadas</span>
                      <div className="p-1.5 bg-indigo-500/20 rounded-lg"><Users className="w-4 h-4 text-indigo-400" /></div>
                    </div>
                    <h3 className="text-3xl font-black text-white">{kpisRRHH.totalEvals}</h3>
                  </div>

                  <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-md shadow-lg flex flex-col justify-center">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-[10px] font-black uppercase tracking-wider">En Plan de Mejora</span>
                      <div className="p-1.5 bg-amber-500/20 rounded-lg"><AlertTriangle className="w-4 h-4 text-amber-400" /></div>
                    </div>
                    <h3 className="text-3xl font-black text-white">{kpisRRHH.enPlanMejora}</h3>
                    <span className="text-xs text-amber-400 font-bold mt-1">Colaboradores &lt; 70%</span>
                  </div>

                  <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-md shadow-lg flex flex-col justify-center">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-[10px] font-black uppercase tracking-wider">Altos Potenciales</span>
                      <div className="p-1.5 bg-purple-500/20 rounded-lg"><Briefcase className="w-4 h-4 text-purple-400" /></div>
                    </div>
                    <h3 className="text-3xl font-black text-white">{kpisRRHH.altosPotenciales}</h3>
                    <span className="text-xs text-purple-300 font-bold mt-1">Desempeño 90%+</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-md">
                    <h4 className="text-white font-bold text-sm mb-6 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-indigo-500"></div> Promedio de Desempeño por Sede
                    </h4>
                    <div className="h-[300px] w-full">
                      {graficosRRHH.dataDesempeno.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={graficosRRHH.dataDesempeno} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                            <XAxis dataKey="sede" stroke="rgba(255,255,255,0.5)" tick={{fill: 'rgba(255,255,255,0.7)', fontSize: 10}} tickLine={false} axisLine={false} />
                            <YAxis stroke="rgba(255,255,255,0.5)" tick={{fill: 'rgba(255,255,255,0.7)', fontSize: 11}} tickLine={false} axisLine={false} domain={[0, 100]} />
                            <Tooltip content={<CustomTooltip />} cursor={{fill: 'rgba(255,255,255,0.05)'}} />
                            <Bar dataKey="promedio" fill="#6366f1" radius={[6, 6, 0, 0]} barSize={40} name="Promedio (%)" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/50 text-sm font-bold">
                          <span className="text-3xl mb-2">📉</span>
                          No hay evaluaciones para los filtros aplicados
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-md">
                    <h4 className="text-white font-bold text-sm mb-6 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div> Historial Evolutivo
                    </h4>
                    <div className="h-[300px] w-full">
                      {graficosRRHH.dataTendenciaAnual.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={graficosRRHH.dataTendenciaAnual} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                              <linearGradient id="colorPuntaje" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                            <XAxis dataKey="mes" stroke="rgba(255,255,255,0.5)" tick={{fill: 'rgba(255,255,255,0.7)', fontSize: 11}} tickLine={false} axisLine={false} />
                            <YAxis stroke="rgba(255,255,255,0.5)" tick={{fill: 'rgba(255,255,255,0.7)', fontSize: 11}} tickLine={false} axisLine={false} domain={['dataMin - 5', 100]} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="puntaje" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorPuntaje)" name="Puntaje Mensual" />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/50 text-sm font-bold">
                          <span className="text-3xl mb-2">📉</span>
                          No hay datos suficientes para la tendencia
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </>
            )}

            {moduloActivo === 'legal' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                <div className="lg:col-span-1 space-y-4">
                  <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-900/40 border border-emerald-500/30 rounded-2xl p-6 shadow-lg">
                    <FileText className="w-8 h-8 text-emerald-400 mb-3" />
                    <h3 className="text-4xl font-black text-white">{kpisLegal.vigentes}</h3>
                    <p className="text-xs text-emerald-200 font-bold uppercase tracking-wider mt-1">Docs. Vigentes</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-500/20 to-amber-900/40 border border-amber-500/30 rounded-2xl p-6 shadow-lg">
                    <Calendar className="w-8 h-8 text-amber-400 mb-3" />
                    <h3 className="text-4xl font-black text-white">{kpisLegal.porVencer}</h3>
                    <p className="text-xs text-amber-200 font-bold uppercase tracking-wider mt-1">Por Vencer (Próx. 30 días)</p>
                  </div>
                  <div className="bg-gradient-to-br from-red-500/20 to-red-900/40 border border-red-500/30 rounded-2xl p-6 shadow-lg">
                    <AlertTriangle className="w-8 h-8 text-red-400 mb-3" />
                    <h3 className="text-4xl font-black text-white">{kpisLegal.vencidos}</h3>
                    <p className="text-xs text-red-200 font-bold uppercase tracking-wider mt-1">Vencidos (Acción Urgente)</p>
                  </div>
                </div>

                <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-md flex flex-col">
                  <h4 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div> Distribución de Estado Normativo
                  </h4>
                  <div className="flex-1 min-h-[300px] relative">
                    {graficosLegal.dataDocumentos.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={graficosLegal.dataDocumentos}
                              cx="50%"
                              cy="50%"
                              innerRadius={80}
                              outerRadius={120}
                              paddingAngle={5}
                              dataKey="value"
                              stroke="none"
                            >
                              {graficosLegal.dataDocumentos.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#fff' }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none pb-8">
                          <span className="block text-3xl font-black text-white">{kpisLegal.total}</span>
                          <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total Docs</span>
                        </div>
                      </>
                    ) : (
                       <div className="w-full h-full flex flex-col items-center justify-center text-white/50 text-sm font-bold">
                         <span className="text-3xl mb-2">📉</span>
                         No hay documentos registrados para esta sede
                       </div>
                    )}
                  </div>
                </div>

              </div>
            )}

            {moduloActivo === 'finanzas' && (
              <div className="h-64 flex flex-col items-center justify-center border-2 border-dashed border-white/20 rounded-3xl bg-black/10">
                <span className="text-5xl mb-4">🚧</span>
                <h3 className="text-xl font-black text-white">Módulo Financiero en Desarrollo</h3>
                <p className="text-slate-400 text-sm mt-2">Aquí conectaremos el Cierre de Mes de Sysplus vs DIAN.</p>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}